/**
 * An emergency admission is registered with `admissionType = 'EMERGENCY'` and
 * may have no bed yet (one can be assigned later). A bed-less admission is
 * treated as an emergency stay as well: DIRECT and REFERRAL admissions require
 * a bed, so this matches how the IPD list labels the bed column.
 */
export function isEmergencyAdmission(
  admission?: { admissionType?: string | null; bed?: unknown } | null,
): boolean {
  if (!admission) return false;
  return (
    String(admission.admissionType ?? '').toUpperCase() === 'EMERGENCY' ||
    !admission.bed
  );
}

/**
 * Statuses whose stay has ended, so the compact details modal (visit summary,
 * billing, receipt) is the right destination from the admissions list instead
 * of the full working admission page.
 *
 * A cancelled admission never had a discharge, but staff still need to review
 * the stay, its billing and print the receipt — the same way they do for a
 * discharged (or Risk Fund) admission.
 */
export function endsVisitInDetailsModal(
  admission?: { status?: string | null } | null,
): boolean {
  const status = String(admission?.status ?? '').toUpperCase();
  return status === 'DISCHARGED' || status === 'CANCELLED';
}

/**
 * `true` while the stay is still running: the patient is in a bed and every
 * open-stay action (discharge, cancel, transfer) must stay available.
 *
 * A bed transfer re-labels the admission as `TRANSFERRED` without ending the
 * stay, so this must never be reduced to `ADMITTED` alone.
 */
export function isOpenStayStatus(status?: string | null): boolean {
  const value = String(status ?? '').toUpperCase();
  return (
    value === 'ADMITTED' ||
    value === 'TRANSFERRED' ||
    value === 'DISCHARGE_INITIATED'
  );
}

/** `true` when the admission was cancelled before it was ever discharged. */
export function isCancelledAdmission(
  admission?: { status?: string | null } | null,
): boolean {
  return String(admission?.status ?? '').toUpperCase() === 'CANCELLED';
}
